import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import Select from "react-select";
import { enqueueSnackbar } from "notistack";

import Breadcrumbs from "../components/Common/Breadcrumb";
import {
  assignReportSummaryAssets,
  exportReportSummaryCsv,
  exportReportSummaryXlsx,
  getReportSummaryCriteria,
  getReportSummaryDryRun,
  getReportSummaryMatchedAssets,
  getReportSummaryMethodology,
  getReportSummaryUnmatchedAssets,
  reassignReportSummaryAssets,
  removeReportSummaryAssets,
} from "../services/ValitanApiService";

const parseData = (response) => response?.data?.data ?? response?.data ?? null;
const parseArray = (response) => {
  const payload = parseData(response);
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const parsePage = (response) => {
  const payload = parseData(response) || {};
  return {
    content: Array.isArray(payload?.content) ? payload.content : [],
    totalPages: Number(payload?.totalPages || payload?.total_pages || 0),
    totalElements: Number(payload?.totalElements || payload?.total_elements || 0),
    number: Number(payload?.number || 0),
    size: Number(payload?.size || 50),
  };
};

const downloadBlobResponse = (response, fallbackName) => {
  const blob = response?.data;
  if (!blob || blob.size === 0) {
    throw new Error("The generated file was empty.");
  }

  const contentDisposition = response?.headers?.["content-disposition"] || "";
  const filenameMatch = contentDisposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
  const filename = decodeURIComponent(filenameMatch?.[1] || fallbackName);
  const objectUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
};

const formatText = (value) => {
  const text = String(value ?? "").trim();
  return text || "N/A";
};

const formatNumber = (value) => new Intl.NumberFormat().format(Number(value || 0));

const matchSourceBadge = (matchSource) => {
  switch (String(matchSource || "").toUpperCase()) {
    case "MANUAL":
      return "success";
    case "MANUAL_UNMATCHED":
      return "warning";
    case "AUTO":
      return "primary";
    default:
      return "secondary";
  }
};

const ReportSummaryManagement = () => {
  document.title = "Report Summary Mapping | Valitan";

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState("");
  const [activeTab, setActiveTab] = useState("unmatched");
  const [criteria, setCriteria] = useState([]);
  const [methodology, setMethodology] = useState(null);
  const [summary, setSummary] = useState([]);
  const [unmatchedGroups, setUnmatchedGroups] = useState([]);
  const [matchedAssetCount, setMatchedAssetCount] = useState(0);
  const [unmatchedAssetCount, setUnmatchedAssetCount] = useState(0);

  const [matchedPage, setMatchedPage] = useState({ content: [], totalPages: 0, totalElements: 0, number: 0, size: 25 });
  const [unmatchedPage, setUnmatchedPage] = useState({ content: [], totalPages: 0, totalElements: 0, number: 0, size: 25 });
  const [matchedLoading, setMatchedLoading] = useState(false);
  const [unmatchedLoading, setUnmatchedLoading] = useState(false);

  const [matchedSearch, setMatchedSearch] = useState("");
  const [unmatchedSearch, setUnmatchedSearch] = useState("");
  const [matchedCriteriaId, setMatchedCriteriaId] = useState("");
  const [targetCriteriaId, setTargetCriteriaId] = useState("");
  const [assignmentNote, setAssignmentNote] = useState("");

  const [selectedMatchedIds, setSelectedMatchedIds] = useState([]);
  const [selectedUnmatchedIds, setSelectedUnmatchedIds] = useState([]);
  const [submittingAction, setSubmittingAction] = useState("");

  const criteriaOptions = useMemo(
    () => criteria.map((item) => ({ value: item.id, label: item.name, code: item.code })),
    [criteria]
  );

  const selectedTargetCriteria = useMemo(
    () => criteriaOptions.find((item) => item.value === targetCriteriaId) || null,
    [criteriaOptions, targetCriteriaId]
  );

  const selectedMatchedCriteria = useMemo(
    () => criteriaOptions.find((item) => item.value === matchedCriteriaId) || null,
    [criteriaOptions, matchedCriteriaId]
  );

  const loadOverview = useCallback(async (useRefreshState = false) => {
    if (useRefreshState) setRefreshing(true);
    try {
      const [criteriaResponse, methodologyResponse, dryRunResponse] = await Promise.all([
        getReportSummaryCriteria(),
        getReportSummaryMethodology(),
        getReportSummaryDryRun(),
      ]);

      const dryRun = parseData(dryRunResponse) || {};
      setCriteria(parseArray(criteriaResponse));
      setMethodology(parseData(methodologyResponse));
      setSummary(Array.isArray(dryRun.summary) ? dryRun.summary : []);
      setUnmatchedGroups(Array.isArray(dryRun.unmatched_groups) ? dryRun.unmatched_groups : []);
      setMatchedAssetCount(Number(dryRun.matched_asset_count || 0));
      setUnmatchedAssetCount(Number(dryRun.unmatched_asset_count || 0));
    } catch (error) {
      enqueueSnackbar("Failed to load report summary configuration.", { variant: "error" });
    } finally {
      if (useRefreshState) setRefreshing(false);
      setLoading(false);
    }
  }, []);

  const loadMatchedAssets = useCallback(async (pageNumber = 0) => {
    setMatchedLoading(true);
    try {
      const response = await getReportSummaryMatchedAssets({
        search: matchedSearch.trim() || undefined,
        criteriaId: matchedCriteriaId || undefined,
        page: pageNumber,
        size: matchedPage.size,
      });
      setMatchedPage(parsePage(response));
      setSelectedMatchedIds([]);
    } catch (error) {
      enqueueSnackbar("Failed to load matched assets.", { variant: "error" });
    } finally {
      setMatchedLoading(false);
    }
  }, [matchedCriteriaId, matchedPage.size, matchedSearch]);

  const loadUnmatchedAssets = useCallback(async (pageNumber = 0) => {
    setUnmatchedLoading(true);
    try {
      const response = await getReportSummaryUnmatchedAssets({
        search: unmatchedSearch.trim() || undefined,
        page: pageNumber,
        size: unmatchedPage.size,
      });
      setUnmatchedPage(parsePage(response));
      setSelectedUnmatchedIds([]);
    } catch (error) {
      enqueueSnackbar("Failed to load unmatched assets.", { variant: "error" });
    } finally {
      setUnmatchedLoading(false);
    }
  }, [unmatchedPage.size, unmatchedSearch]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    loadMatchedAssets(0);
  }, [loadMatchedAssets]);

  useEffect(() => {
    loadUnmatchedAssets(0);
  }, [loadUnmatchedAssets]);

  const refreshAll = async () => {
    await Promise.all([
      loadOverview(true),
      loadMatchedAssets(0),
      loadUnmatchedAssets(0),
    ]);
  };

  const toggleSelected = (setter, current, assetId) => {
    setter(
      current.includes(assetId)
        ? current.filter((item) => item !== assetId)
        : [...current, assetId]
    );
  };

  const replaceSelected = (setter, rows) => {
    setter(rows.map((item) => item.id).filter(Boolean));
  };

  const clearSelected = (setter) => {
    setter([]);
  };

  const runAssignment = async (mode) => {
    const assetIds = mode === "remove" ? selectedMatchedIds : activeTab === "matched" ? selectedMatchedIds : selectedUnmatchedIds;

    if (!assetIds.length) {
      enqueueSnackbar("Select at least one asset.", { variant: "warning" });
      return;
    }

    if (mode !== "remove" && !targetCriteriaId) {
      enqueueSnackbar("Select a target report criteria.", { variant: "warning" });
      return;
    }

    setSubmittingAction(mode);
    try {
      if (mode === "assign") {
        await assignReportSummaryAssets({ assetIds, criteriaId: targetCriteriaId, note: assignmentNote.trim() || null });
      } else if (mode === "reassign") {
        await reassignReportSummaryAssets({ assetIds, criteriaId: targetCriteriaId, note: assignmentNote.trim() || null });
      } else {
        await removeReportSummaryAssets({ assetIds, note: assignmentNote.trim() || null });
      }

      enqueueSnackbar("Report summary mapping updated.", { variant: "success" });
      setAssignmentNote("");
      await refreshAll();
    } catch (error) {
      enqueueSnackbar("Failed to update report summary mapping.", { variant: "error" });
    } finally {
      setSubmittingAction("");
    }
  };

  const handleExport = async (type) => {
    setExporting(type);
    try {
      const response = type === "csv" ? await exportReportSummaryCsv() : await exportReportSummaryXlsx();
      downloadBlobResponse(
        response,
        `report_summary_${new Date().toISOString().slice(0, 10)}.${type === "csv" ? "csv" : "xlsx"}`
      );
      enqueueSnackbar("Report summary export generated.", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Failed to export report summary.", { variant: "error" });
    } finally {
      setExporting("");
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Asset Valuation" breadcrumbItem="Report Summary Mapping" />

        <Row className="g-3 mb-3">
          <Col lg={8}>
            <Card className="h-100">
              <CardHeader className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                <div>
                  <h4 className="card-title mb-1">Dedicated Report Summary Mapping</h4>
                  <p className="text-muted mb-0">
                    Match unmatched assets into persisted report criteria and review the live summary before export.
                  </p>
                </div>
                <div className="d-flex gap-2 flex-wrap">
                  <Button color="light" onClick={() => refreshAll()} disabled={refreshing || loading}>
                    {refreshing ? <Spinner size="sm" /> : <i className="ri-refresh-line" />}
                  </Button>
                  <Button color="primary" onClick={() => handleExport("xlsx")} disabled={exporting === "xlsx" || loading}>
                    {exporting === "xlsx" ? <Spinner size="sm" className="me-2" /> : <i className="ri-file-excel-2-line me-2" />}
                    Export XLSX
                  </Button>
                  <Button color="secondary" onClick={() => handleExport("csv")} disabled={exporting === "csv" || loading}>
                    {exporting === "csv" ? <Spinner size="sm" className="me-2" /> : <i className="ri-file-text-line me-2" />}
                    Export CSV
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                <Row className="g-3">
                  <Col md={4}>
                    <div className="border rounded p-3 h-100">
                      <div className="text-muted small mb-1">Matched assets</div>
                      <div className="fs-3 fw-semibold">{formatNumber(matchedAssetCount)}</div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="border rounded p-3 h-100">
                      <div className="text-muted small mb-1">Unmatched assets</div>
                      <div className="fs-3 fw-semibold">{formatNumber(unmatchedAssetCount)}</div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="border rounded p-3 h-100">
                      <div className="text-muted small mb-1">Persisted criteria</div>
                      <div className="fs-3 fw-semibold">{formatNumber(criteria.length)}</div>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>

          <Col lg={4}>
            <Card className="h-100">
              <CardHeader>
                <h5 className="card-title mb-0">How It Works</h5>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="text-center py-4"><Spinner /></div>
                ) : (
                  <>
                    <p className="text-muted">{formatText(methodology?.ui_goal)}</p>
                    <div className="mb-3">
                      <div className="fw-semibold mb-2">Filters Applied</div>
                      <ul className="mb-0 ps-3">
                        {(methodology?.filters_applied || []).map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <div className="fw-semibold mb-2">Assignment Order</div>
                      <ul className="mb-0 ps-3">
                        {(methodology?.assignment_precedence || []).map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row className="g-3 mb-3">
          <Col lg={8}>
            <Card>
              <CardHeader>
                <h5 className="card-title mb-0">Summary Preview</h5>
              </CardHeader>
              <CardBody className="table-responsive">
                <Table className="align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Criteria</th>
                      <th>Asset Records</th>
                      <th>Total Units</th>
                      <th>Distinct Institutions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.map((row) => (
                      <tr key={row.criteria_id || row.criteria_code}>
                        <td>
                          <div className="fw-semibold">{formatText(row.criteria_name)}</div>
                          <div className="text-muted small">{formatText(row.criteria_code)}</div>
                        </td>
                        <td>{formatNumber(row.asset_records)}</td>
                        <td>{formatNumber(row.total_units)}</td>
                        <td>{formatNumber(row.distinct_institutions)}</td>
                      </tr>
                    ))}
                    {!summary.length && (
                      <tr>
                        <td colSpan="4" className="text-center text-muted py-4">No summary rows found.</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>

          <Col lg={4}>
            <Card>
              <CardHeader>
                <h5 className="card-title mb-0">Top Unmatched Groups</h5>
              </CardHeader>
              <CardBody className="p-0">
                <div className="table-responsive">
                  <Table className="align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Group</th>
                        <th>Assets</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unmatchedGroups.slice(0, 8).map((group, index) => (
                        <tr key={`${group.asset_category_code}-${group.department_code}-${group.institution_name}-${index}`}>
                          <td>
                            <div className="fw-semibold">{formatText(group.institution_name)}</div>
                            <div className="text-muted small">
                              {formatText(group.asset_category_code)} | {formatText(group.department_code)} | {formatText(group.institution_category)}
                            </div>
                          </td>
                          <td>{formatNumber(group.asset_count)}</td>
                        </tr>
                      ))}
                      {!unmatchedGroups.length && (
                        <tr>
                          <td colSpan="2" className="text-center text-muted py-4">No unmatched groups.</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Card>
          <CardHeader>
            <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
              <Nav tabs>
                <NavItem>
                  <NavLink
                    href="#"
                    active={activeTab === "unmatched"}
                    onClick={(event) => {
                      event.preventDefault();
                      setActiveTab("unmatched");
                    }}
                  >
                    Unmatched Assets
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    href="#"
                    active={activeTab === "matched"}
                    onClick={(event) => {
                      event.preventDefault();
                      setActiveTab("matched");
                    }}
                  >
                    Matched Assets
                  </NavLink>
                </NavItem>
              </Nav>
              <div className="text-muted small">
                Use the tabs to add unmatched assets into a target criteria or remove or reassign matched assets.
              </div>
            </div>
          </CardHeader>
          <CardBody>
            <Row className="g-3 mb-3">
              <Col lg={4}>
                <Label className="form-label">Target Criteria</Label>
                <Select
                  options={criteriaOptions}
                  value={selectedTargetCriteria}
                  onChange={(option) => setTargetCriteriaId(option?.value || "")}
                  placeholder="Select target criteria"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                />
              </Col>
              <Col lg={4}>
                <Label className="form-label">Assignment Note</Label>
                <Input
                  value={assignmentNote}
                  onChange={(event) => setAssignmentNote(event.target.value)}
                  placeholder="Optional note for this mapping"
                />
              </Col>
              <Col lg={4}>
                <Label className="form-label">Actions</Label>
                <div className="d-flex gap-2 flex-wrap mb-2">
                  {activeTab === "unmatched" ? (
                    <Button color="primary" onClick={() => runAssignment("assign")} disabled={submittingAction === "assign"}>
                      {submittingAction === "assign" ? <Spinner size="sm" className="me-2" /> : null}
                      Assign Selected
                    </Button>
                  ) : (
                    <>
                      <Button color="primary" onClick={() => runAssignment("reassign")} disabled={submittingAction === "reassign"}>
                        {submittingAction === "reassign" ? <Spinner size="sm" className="me-2" /> : null}
                        Reassign Selected
                      </Button>
                      <Button color="warning" onClick={() => runAssignment("remove")} disabled={submittingAction === "remove"}>
                        {submittingAction === "remove" ? <Spinner size="sm" className="me-2" /> : null}
                        Move To Unmatched
                      </Button>
                    </>
                  )}
                </div>
                <div className="d-flex gap-2 flex-wrap">
                  {activeTab === "unmatched" ? (
                    <>
                      <Button color="light" onClick={() => replaceSelected(setSelectedUnmatchedIds, unmatchedPage.content)} disabled={unmatchedLoading || !unmatchedPage.content.length}>
                        Select Visible
                      </Button>
                      <Button color="light" onClick={() => clearSelected(setSelectedUnmatchedIds)} disabled={!selectedUnmatchedIds.length}>
                        Clear Selection
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button color="light" onClick={() => replaceSelected(setSelectedMatchedIds, matchedPage.content)} disabled={matchedLoading || !matchedPage.content.length}>
                        Select Visible
                      </Button>
                      <Button color="light" onClick={() => clearSelected(setSelectedMatchedIds)} disabled={!selectedMatchedIds.length}>
                        Clear Selection
                      </Button>
                    </>
                  )}
                </div>
              </Col>
            </Row>

            {activeTab === "unmatched" ? (
              <>
                <Row className="g-3 mb-3">
                  <Col lg={6}>
                    <Label className="form-label">Search Unmatched Assets</Label>
                    <Input
                      value={unmatchedSearch}
                      onChange={(event) => setUnmatchedSearch(event.target.value)}
                      placeholder="Search barcode, name, description, institution"
                    />
                  </Col>
                </Row>

                <Alert color="info">
                  Unmatched assets remain outside the report summary until you explicitly assign them into one of the persisted criteria.
                </Alert>

                <div className="table-responsive">
                  <Table className="align-middle mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: 100 }}>Pick</th>
                        <th>Asset</th>
                        <th>Institution</th>
                        <th>Category</th>
                        <th>Department</th>
                        <th>Units</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unmatchedLoading ? (
                        <tr><td colSpan="6" className="text-center py-4"><Spinner /></td></tr>
                      ) : unmatchedPage.content.map((asset) => (
                        <tr key={asset.id}>
                          <td>
                            <Button
                              color={selectedUnmatchedIds.includes(asset.id) ? "primary" : "light"}
                              size="sm"
                              onClick={() => toggleSelected(setSelectedUnmatchedIds, selectedUnmatchedIds, asset.id)}
                            >
                              {selectedUnmatchedIds.includes(asset.id) ? "Selected" : "Select"}
                            </Button>
                          </td>
                          <td>
                            <div className="fw-semibold">{formatText(asset.name)}</div>
                            <div className="text-muted small">{formatText(asset.barcode)}</div>
                            <div className="text-muted small">{formatText(asset.description)}</div>
                            <div className="text-muted small">Remark: {formatText(asset.remark)}</div>
                          </td>
                          <td>
                            <div>{formatText(asset.institution_name)}</div>
                            <div className="text-muted small">{formatText(asset.institution_category)}</div>
                          </td>
                          <td>
                            <div>{formatText(asset.asset_category_name)}</div>
                            <div className="text-muted small">{formatText(asset.asset_category_code)}</div>
                          </td>
                          <td>
                            <div>{formatText(asset.department_name)}</div>
                            <div className="text-muted small">{formatText(asset.department_code)}</div>
                          </td>
                          <td>{formatNumber(asset.number_of_buildings || asset.quantity || 1)}</td>
                        </tr>
                      ))}
                      {!unmatchedLoading && !unmatchedPage.content.length && (
                        <tr><td colSpan="6" className="text-center text-muted py-4">No unmatched assets found.</td></tr>
                      )}
                    </tbody>
                  </Table>
                </div>

                <div className="d-flex justify-content-between align-items-center mt-3 gap-3 flex-wrap">
                  <div className="text-muted small">
                    Showing {formatNumber(unmatchedPage.content.length)} of {formatNumber(unmatchedPage.totalElements)} unmatched assets
                  </div>
                  <div className="d-flex gap-2">
                    <Button color="light" disabled={unmatchedPage.number <= 0 || unmatchedLoading} onClick={() => loadUnmatchedAssets(Math.max(unmatchedPage.number - 1, 0))}>
                      Previous
                    </Button>
                    <Button color="light" disabled={unmatchedPage.number + 1 >= unmatchedPage.totalPages || unmatchedLoading} onClick={() => loadUnmatchedAssets(unmatchedPage.number + 1)}>
                      Next
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <Row className="g-3 mb-3">
                  <Col lg={4}>
                    <Label className="form-label">Search Matched Assets</Label>
                    <Input
                      value={matchedSearch}
                      onChange={(event) => setMatchedSearch(event.target.value)}
                      placeholder="Search barcode, name, institution, criteria"
                    />
                  </Col>
                  <Col lg={4}>
                    <Label className="form-label">Filter By Current Criteria</Label>
                    <Select
                      options={criteriaOptions}
                      value={selectedMatchedCriteria}
                      onChange={(option) => setMatchedCriteriaId(option?.value || "")}
                      placeholder="All matched criteria"
                      isClearable
                      menuPortalTarget={document.body}
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                  </Col>
                </Row>

                <div className="table-responsive">
                  <Table className="align-middle mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: 100 }}>Pick</th>
                        <th>Asset</th>
                        <th>Current Match</th>
                        <th>Institution</th>
                        <th>Department</th>
                        <th>Units</th>
                      </tr>
                    </thead>
                    <tbody>
                      {matchedLoading ? (
                        <tr><td colSpan="6" className="text-center py-4"><Spinner /></td></tr>
                      ) : matchedPage.content.map((asset) => (
                        <tr key={asset.id}>
                          <td>
                            <Button
                              color={selectedMatchedIds.includes(asset.id) ? "primary" : "light"}
                              size="sm"
                              onClick={() => toggleSelected(setSelectedMatchedIds, selectedMatchedIds, asset.id)}
                            >
                              {selectedMatchedIds.includes(asset.id) ? "Selected" : "Select"}
                            </Button>
                          </td>
                          <td>
                            <div className="fw-semibold">{formatText(asset.name)}</div>
                            <div className="text-muted small">{formatText(asset.barcode)}</div>
                            <div className="text-muted small">{formatText(asset.asset_category_name)} | {formatText(asset.asset_category_code)}</div>
                          </td>
                          <td>
                            <div className="fw-semibold">{formatText(asset.effective_criteria_name)}</div>
                            <div className="text-muted small mb-1">{formatText(asset.effective_criteria_code)}</div>
                            <Badge color={matchSourceBadge(asset.match_source)}>{formatText(asset.match_source)}</Badge>
                          </td>
                          <td>
                            <div>{formatText(asset.institution_name)}</div>
                            <div className="text-muted small">{formatText(asset.institution_category)}</div>
                          </td>
                          <td>
                            <div>{formatText(asset.department_name)}</div>
                            <div className="text-muted small">{formatText(asset.department_code)}</div>
                          </td>
                          <td>{formatNumber(asset.number_of_buildings || asset.quantity || 1)}</td>
                        </tr>
                      ))}
                      {!matchedLoading && !matchedPage.content.length && (
                        <tr><td colSpan="6" className="text-center text-muted py-4">No matched assets found.</td></tr>
                      )}
                    </tbody>
                  </Table>
                </div>

                <div className="d-flex justify-content-between align-items-center mt-3 gap-3 flex-wrap">
                  <div className="text-muted small">
                    Showing {formatNumber(matchedPage.content.length)} of {formatNumber(matchedPage.totalElements)} matched assets
                  </div>
                  <div className="d-flex gap-2">
                    <Button color="light" disabled={matchedPage.number <= 0 || matchedLoading} onClick={() => loadMatchedAssets(Math.max(matchedPage.number - 1, 0))}>
                      Previous
                    </Button>
                    <Button color="light" disabled={matchedPage.number + 1 >= matchedPage.totalPages || matchedLoading} onClick={() => loadMatchedAssets(matchedPage.number + 1)}>
                      Next
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default ReportSummaryManagement;
